import React from "react";

import type { AxiosRequestConfig } from "axios";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";

import {
  IconCalendarFill,
  IconFeedbackFill,
  IconFileAvi,
  IconFileDoc,
  IconFileDocx,
  IconFileGeoTiff,
  IconFileIfc,
  IconFileLas,
  IconFileLaz,
  IconFileMp4,
  IconFilePdf,
  IconFileShp,
  IconFileTif,
  IconFileTiff,
  IconFileTxt,
  IconFileUndefined,
  IconFileXls,
  IconFileZip,
  IconPinFill,
  IconTools,
} from "@synthesis/shared-icons";
import { downloadDocumentById, type FileMetaDto } from "../../../api";
import { Checkbox, ContextMenu, Tooltip, type ContextMenuItem } from "@synthesis/shared-ui";
import { convertBlobToBase64, cn, logger } from "@synthesis/shared-utils";

import { type Checked, useStores } from "../../../store";
import { DocumentsTypes } from "../../../types";
import { useMarkUp } from "../hooks";

const Wrapper = ({ children, ...rest }: React.HTMLAttributes<HTMLElement>) => {
  return (
    <section className="mb-16 grid gap-4" {...rest}>
      {children}
    </section>
  );
};

const IMAGE_VARIANTS = { png: "png", jpg: "jpg", jpeg: "jpeg", bmp: "bmp", svg: "svg", webp: "webp" } as const;

const isImageExt = (ext: string | null | undefined): ext is keyof typeof IMAGE_VARIANTS => !!ext && ext.toLowerCase() in IMAGE_VARIANTS;

type FileProps = {
  data: FileMetaDto & { progress?: number };
  onClickModel: (data: FileMetaDto) => void;
  onContextMenu: (e: React.MouseEvent, data: FileMetaDto) => void;
  onClickFile: (e: React.MouseEvent, imgSrc: string | null, data: FileMetaDto) => void;
  isEngineering: boolean;
  isModel: boolean;
  isUsingInGantt: boolean;
  setSelectedFile: (data: unknown) => void;
  state: ContextMenuItem[];
};

const File = observer<FileProps>(({ onClickModel, data, isUsingInGantt, isEngineering, state, setSelectedFile, isModel, onClickFile }) => {
  const { generalStore } = useStores();
  const [imgSrc, setImgSrc] = React.useState<string | null>(null);
  const { isLoading, markupsCount } = useMarkUp(data.id);

  const handleCheck = (checkedData: Checked) => {
    generalStore.setCheckedContents(checkedData);
  };

  const isChecked = React.useMemo(() => generalStore.checkedFiles.get(data.id) !== undefined, [generalStore.checkedFiles, data]);

  React.useEffect(() => {
    (async () => {
      if (isImageExt(data.ext)) {
        try {
          const config: AxiosRequestConfig = { responseType: "blob" };
          const imageBlob = await downloadDocumentById(data.id, config);
          const base64Src = await convertBlobToBase64(imageBlob);
          setImgSrc(base64Src as string);
        } catch (err) {
          logger.error("downloadImageFile", err);
        }
      }
    })();
  }, [data, imgSrc]);

  const icon = React.useMemo(() => {
    const ext = data.ext?.toLowerCase();

    if (ext === "pdf") return <IconFilePdf className="text-neutrals-90 size-4" />;
    if (ext === "doc") return <IconFileDoc className="text-neutrals-90 size-4" />;
    if (ext === "docx") return <IconFileDocx className="text-neutrals-90 size-4" />;
    if (ext === "ifc") return <IconFileIfc className="text-neutrals-90 size-4" />;
    if (ext === "tiff") return <IconFileTiff className="text-neutrals-90 size-4" />;
    if (ext === "geotiff") return <IconFileGeoTiff className="text-neutrals-90 size-4" />;
    if (ext === "tif") return <IconFileTif className="text-neutrals-90 size-4" />;
    if (ext === "avi") return <IconFileAvi className="text-neutrals-90 size-4" />;
    if (ext === "mp4") return <IconFileMp4 className="text-neutrals-90 size-4" />;
    if (ext === "las") return <IconFileLas className="text-neutrals-90 size-4" />;
    if (ext === "laz") return <IconFileLaz className="text-neutrals-90 size-4" />;
    if (ext === "shp") return <IconFileShp className="text-neutrals-90 size-4" />;
    if (ext === "zip") return <IconFileZip className="text-neutrals-90 size-4" />;
    if (ext === "txt") return <IconFileTxt className="text-neutrals-90 size-4" />;
    if (ext === "xls") return <IconFileXls className="text-neutrals-90 size-4" />;
    if (ext === "xlsx") return <IconFileXls className="text-neutrals-90 size-4" />;
    return <IconFileUndefined className="text-neutrals-90 size-4" />;
  }, [data.ext]);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (data.type === DocumentsTypes.model) onClickModel(data);
    if (data.type !== DocumentsTypes.model) onClickFile(e, imgSrc, data);
  };

  const additional = (
    <div className="flex items-center justify-center gap-2">
      {(isEngineering || isModel || isUsingInGantt) && (
        <div className="bg-tertiary-90 flex gap-1 rounded-lg p-[0.375rem]">
          {isEngineering && (
            <Tooltip arrowSide="top" arrowAlign="center" description="Инженерное изыскание">
              <figure>
                <IconPinFill className="text-tertiary-60 size-4" />
              </figure>
            </Tooltip>
          )}
          {isModel && (
            <Tooltip arrowSide="top" arrowAlign="center" description="Основная модель">
              <figure>
                <IconTools className="text-tertiary-60 size-4" />
              </figure>
            </Tooltip>
          )}
          {isUsingInGantt && (
            <Tooltip arrowSide="top" arrowAlign="center" description="Участвует в сетевом графике">
              <figure>
                <IconCalendarFill className="text-tertiary-60 size-4" />
              </figure>
            </Tooltip>
          )}
        </div>
      )}
      {(markupsCount ?? 0) > 0 && (
        <>
          <div className="bg-system-error-50 flex gap-1 rounded-lg p-[0.375rem]">
            <Tooltip arrowSide="top" arrowAlign="center" title="Доступных замечаний:" description={markupsCount}>
              <figure>
                <IconFeedbackFill className="text-system-error-90 size-4" />
              </figure>
            </Tooltip>
          </div>
          {!isLoading && (markupsCount ?? 0) > 0 && <p className="text-system-error-50 paragraph-sm">Замечаний ({markupsCount})</p>}
        </>
      )}
    </div>
  );

  return (
    <ContextMenu state={state} onClick={() => setSelectedFile(data)}>
      <Container isChecked={isChecked} onClick={handleClick}>
        <figure className="relative">
          {imgSrc ? (
            <div className="relative flex size-4 items-center justify-center">
              <img className="shrink-0 grow object-contain object-center" src={imgSrc} alt="" />
            </div>
          ) : (
            icon
          )}
        </figure>

        <h2 className="text-type-common paragraph-sm break-words">{data.baseName}</h2>

        {additional}

        <div className="ml-auto flex shrink-0 items-center gap-4">
          <p className="text-type-light paragraph-sm min-w-[75px]">Версия {data.version}</p>
          <p className="text-type-light paragraph-sm">{dayjs(data.createdAt).format("DD.MM.YYYY HH:mm:ss")}</p>
          <p className="text-type-light paragraph-sm min-w-[140px]">{dayjs(data.modifiedAt).format("DD.MM.YYYY HH:mm:ss")}</p>
        </div>

        <CheckboxWrapper isChecked={isChecked} onClick={(e) => e.stopPropagation()}>
          <Checkbox onChange={() => handleCheck(data)} checked={isChecked} />
        </CheckboxWrapper>
      </Container>
    </ContextMenu>
  );
});

const CheckboxWrapper = ({ isChecked, className, ...props }: React.HTMLAttributes<HTMLDivElement> & { isChecked?: boolean }) => (
  <div className={cn("absolute -left-6 top-2 z-30 opacity-0 group-hover:opacity-100", isChecked && "opacity-100", className)} {...props} />
);

type ContainerProps = React.HTMLAttributes<HTMLDivElement> & { isChecked: boolean };

const Container = ({ isChecked, className, ...props }: ContainerProps) => (
  <div
    className={cn(
      "hover:bg-neutrals-99 group relative flex w-full cursor-pointer items-center gap-2 rounded-[14px] border-b border-transparent px-3 py-2 transition-colors",
      isChecked && "bg-tertiary-95 border-tertiary-99 hover:bg-tertiary-95/75 first:rounded-r-[14px] last:rounded-b-[14px]",
      className,
    )}
    {...props}
  />
);

const ListView = {
  File,
  Wrapper,
};

export default ListView;
